import React, { useContext, useEffect, useState } from "react";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Group } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/groups_pb";

import { DEFAULT_REGION_CODE } from "../constants/index";
import { getOktaUserId } from "../helpers/get-okta-user-id";
import { PermissionsContext } from "./permissions-context";
import { SnackbarContext } from "./snackbar-context";
import {
  listUserAllowedActions,
  listUserOktaGroups,
} from "api/endpoints/periscope-admin";
import { callOptionsHeaders } from "api/helpers";

interface PermissionsContextWrapperProps {
  children: React.ReactNode;
}

const PermissionsContextWrapper = ({
  children,
}: PermissionsContextWrapperProps) => {
  const [permissions, setPermissions] = useState({
    userAllowedActions: [] as ActionType[],
    isPermissionsLoading: true,
  });
  const [groups, setGroups] = useState({
    userGroups: [] as string[],
    isGroupsLoading: true,
  });

  const { showError } = useContext(SnackbarContext);

  const fetchUserAllowedActions = (oktaUserId: string) => {
    let updatedPermissions = { ...permissions };
    updatedPermissions.isPermissionsLoading = true;
    setPermissions(updatedPermissions);

    listUserAllowedActions(
      { oktaUserId },
      callOptionsHeaders(DEFAULT_REGION_CODE)
    )
      .then((availableActionsResp) => {
        updatedPermissions = {
          ...permissions,
        };
        updatedPermissions.userAllowedActions = availableActionsResp;
        setPermissions(updatedPermissions);
      })
      .catch(showError)
      .finally(() => {
        updatedPermissions.isPermissionsLoading = false;
        setPermissions(updatedPermissions);
      });
  };

  const fetchUserGroups = () => {
    let updatedGroups = { ...groups };
    updatedGroups.isGroupsLoading = true;
    setGroups(updatedGroups);

    listUserOktaGroups(callOptionsHeaders(DEFAULT_REGION_CODE))
      .then((resp) => {
        updatedGroups = {
          ...groups,
        };
        const userGroupNames = resp.map((group: Group) => group.name);
        updatedGroups.userGroups = userGroupNames;
        setGroups(updatedGroups);
      })
      .catch(showError)
      .finally(() => {
        updatedGroups.isGroupsLoading = false;
        setGroups(updatedGroups);
      });
  };

  const hasPermissions = (actions: ActionType[]) => {
    return actions.every((action) =>
      permissions.userAllowedActions.includes(action)
    );
  };
  const hasGroups = (groupNames: string[]) => {
    return groupNames.some((group) => {
      return groups.userGroups.includes(group);
    });
  };

  useEffect(() => {
    getOktaUserId()
      .then((oktaUserId) => {
        fetchUserAllowedActions(oktaUserId);
      })
      .then(() => {
        fetchUserGroups();
      })
      // eslint-disable-next-line
      .catch((error) => console.log((error as any).message));
    // eslint-disable-next-line
  }, [getOktaUserId]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        setPermissions,
        hasPermissions,
        groups,
        hasGroups,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContextWrapper;
